import React, { useEffect, useState, useCallback } from 'react';
import {
  Layout, Table, Button, Tag, Space, Modal, Input, message, Card, Select, Badge, Typography
} from 'antd';
import {
  CheckOutlined, CloseOutlined, LogoutOutlined, HistoryOutlined, ApiOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  getApplications, approveApplication, rejectApplication, batchApprove, getGitHubStatus
} from '../../services/api';

const { Header, Content } = Layout;
const { Title } = Typography;

const statusMap: Record<string, { color: string; text: string }> = {
  PENDING: { color: 'processing', text: '待审核' },
  APPROVED: { color: 'success', text: '已通过' },
  REJECTED: { color: 'error', text: '已拒绝' },
  INVITED: { color: 'success', text: '已邀请' },
  FAILED: { color: 'error', text: '邀请失败' },
};

const DashboardPage: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [githubStatus, setGithubStatus] = useState<string>('unknown');
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: page - 1, size: pageSize };
      if (statusFilter) {
        params.status = statusFilter;
      }
      const res = await getApplications(params);
      setData(res.data.content);
      setTotal(res.data.totalElements);
    } catch {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  const checkGitHubStatus = async () => {
    try {
      const res = await getGitHubStatus();
      setGithubStatus(res.data.status);
    } catch {
      setGithubStatus('error');
    }
  };

  useEffect(() => {
    fetchData();
    checkGitHubStatus();
  }, [fetchData]);

  const handleApprove = async (id: number) => {
    try {
      await approveApplication(id);
      message.success('审批通过，邀请已发送');
      fetchData();
    } catch (err: any) {
      message.error(err.response?.data?.message || '操作失败');
    }
  };

  const handleReject = async () => {
    if (rejectingId === null) return;
    try {
      await rejectApplication(rejectingId, rejectReason);
      message.success('已拒绝');
      setRejectModalVisible(false);
      setRejectReason('');
      setRejectingId(null);
      fetchData();
    } catch {
      message.error('操作失败');
    }
  };

  const handleBatchApprove = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要审批的申请');
      return;
    }
    Modal.confirm({
      title: '批量审批确认',
      content: `确定要通过选中的 ${selectedRowKeys.length} 个申请吗？`,
      onOk: async () => {
        try {
          await batchApprove(selectedRowKeys as number[]);
          message.success('批量审批成功');
          setSelectedRowKeys([]);
          fetchData();
        } catch {
          message.error('批量审批失败');
        }
      },
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '邮箱', dataIndex: 'email', key: 'email' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusMap[status]?.color}>{statusMap[status]?.text || status}</Tag>
      ),
    },
    {
      title: '提交时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (t: string) => new Date(t).toLocaleString('zh-CN'),
    },
    {
      title: '审核时间',
      dataIndex: 'reviewedAt',
      key: 'reviewedAt',
      render: (t: string) => t ? new Date(t).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: any) => (
        <Space>
          {record.status === 'PENDING' && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleApprove(record.id)}
              >
                通过
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  setRejectingId(record.id);
                  setRejectModalVisible(true);
                }}
              >
                拒绝
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    getCheckboxProps: (record: any) => ({
      disabled: record.status !== 'PENDING',
    }),
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '0 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
        <Title level={4} style={{ margin: 0 }}>GitHub 组织邀请管理</Title>
        <Space>
          <Badge status={githubStatus === 'connected' ? 'success' : 'error'} text={`GitHub: ${githubStatus === 'connected' ? '已连接' : '未连接'}`} />
          <Button icon={<ApiOutlined />} onClick={checkGitHubStatus}>刷新状态</Button>
          <Button icon={<HistoryOutlined />} onClick={() => navigate('/admin/history')}>邀请历史</Button>
          <Button icon={<LogoutOutlined />} onClick={handleLogout}>退出</Button>
        </Space>
      </Header>
      <Content style={{ padding: 24 }}>
        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Select
              allowClear
              placeholder="按状态筛选"
              style={{ width: 150 }}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
            >
              <Select.Option value="PENDING">待审核</Select.Option>
              <Select.Option value="APPROVED">已通过</Select.Option>
              <Select.Option value="REJECTED">已拒绝</Select.Option>
              <Select.Option value="INVITED">已邀请</Select.Option>
              <Select.Option value="FAILED">邀请失败</Select.Option>
            </Select>
            <Button
              type="primary"
              onClick={handleBatchApprove}
              disabled={selectedRowKeys.length === 0}
            >
              批量通过 ({selectedRowKeys.length})
            </Button>
          </Space>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            }}
          />
        </Card>
      </Content>

      <Modal
        title="拒绝申请"
        open={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => { setRejectModalVisible(false); setRejectReason(''); setRejectingId(null); }}
      >
        <Input.TextArea
          rows={3}
          placeholder="请输入拒绝原因（可选）"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </Layout>
  );
};

export default DashboardPage;
